import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { shouldHideSubscriptionButton } from '../utils/subscriptionPlan';
import { applyMailSettings, getMailSettings } from '../utils/mailSettings';
import UserAvatar from './UserAvatar';
import ProfilePhotoEditor from './ProfilePhotoEditor';
import { WorkspaceLauncher } from './workspace';
import SearchOptionsPanel from './search/SearchOptionsPanel';
import './Navigation.css';
import './UserAvatar.css';

/**
 * Navigation Component
 * Gmail-style header with search and user info
 */
const Navigation = ({
  currentUser,
  onLogout,
  onSwitchAccount,
  onDeleteAccount,
  onProfileUpdate,
  allAccounts = [],
  onMenuClick,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const [showWorkspace, setShowWorkspace] = useState(false);
  const [showSearchOptions, setShowSearchOptions] = useState(false);
  const [showMoreAccounts, setShowMoreAccounts] = useState(true);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackName, setFeedbackName] = useState('');
  const [feedbackStatus, setFeedbackStatus] = useState('');
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [hideSubscriptionButton, setHideSubscriptionButton] = useState(shouldHideSubscriptionButton);
  const navigate = useNavigate();
  const location = useLocation();
  const profilePopupRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const workspaceBtnRef = useRef(null);

  useEffect(() => {
    applyMailSettings(getMailSettings());
  }, []);

  useEffect(() => {
    const syncSubscriptionButton = () => {
      setHideSubscriptionButton(shouldHideSubscriptionButton());
    };

    syncSubscriptionButton();
    window.addEventListener('atozas-subscription-change', syncSubscriptionButton);
    window.addEventListener('storage', syncSubscriptionButton);

    return () => {
      window.removeEventListener('atozas-subscription-change', syncSubscriptionButton);
      window.removeEventListener('storage', syncSubscriptionButton);
    };
  }, []);

  // Sync search query with URL when on search page
  useEffect(() => {
    if (location.pathname === '/search') {
      const params = new URLSearchParams(location.search);
      const q = params.get('q') || '';
      setSearchQuery(q);
    } else {
      setSearchQuery('');
    }
  }, [location]);

  // Fast debounced search - triggers search as user types
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If search query is empty, navigate back to inbox
    if (!searchQuery.trim()) {
      if (location.pathname === '/search') {
        navigate('/inbox');
      }
      return;
    }

    // For single character, use shorter delay (150ms) for fast response
    // For longer queries, use normal delay (300ms)
    const delay = searchQuery.trim().length <= 2 ? 200 : 350;

    // Set timeout to navigate after user stops typing
    searchTimeoutRef.current = setTimeout(() => {
      // Replace while already on the results page, so a search doesn't leave one
      // history entry per keystroke for the user to back out through.
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`, {
        replace: location.pathname === '/search',
      });
    }, delay);

    // Cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, navigate, location.pathname]);

  /** Run a query now, bypassing the debounce (Enter, or the options panel). */
  const runSearch = (query) => {
    const trimmed = String(query || '').trim();
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (!trimmed) return;
    setSearchQuery(trimmed);
    navigate(`/search?q=${encodeURIComponent(trimmed)}`, {
      replace: location.pathname === '/search',
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    runSearch(searchQuery);
  };

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profilePopupRef.current && !profilePopupRef.current.contains(event.target)) {
        setShowProfilePopup(false);
      }
    };

    if (showProfilePopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfilePopup]);

  const toggleProfilePopup = () => {
    setShowProfilePopup(!showProfilePopup);
  };

  // Workspace Launcher: toggle button + Ctrl+Shift+W keyboard shortcut.
  const toggleWorkspace = () => {
    setShowWorkspace((prev) => !prev);
    setShowProfilePopup(false);
  };

  useEffect(() => {
    const handleShortcut = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && (event.key === 'W' || event.key === 'w')) {
        event.preventDefault();
        setShowWorkspace((prev) => {
          if (!prev) setShowProfilePopup(false);
          return !prev;
        });
      }
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, []);

  const handleSignOutAll = async () => {
    setShowProfilePopup(false);

    try {
      const deviceId = localStorage.getItem('deviceId');
      if (deviceId) {
        await axiosInstance.post('/api/auth/sign-out-all', { deviceId });
      }
    } catch (err) {
      console.error('Sign out all accounts:', err);
    }

    // Await logout so the server-side ATOZAS SSO session is fully destroyed
    // BEFORE we navigate. A premature redirect cancels the in-flight request
    // and the user gets silently re-logged in via /me on the next page load.
    await onLogout();
    window.location.replace('/');
  };

  const handleAddAnotherAccount = () => {
    setShowProfilePopup(false);
    // Navigate to login page to add another account
    navigate('/login?addAccount=true');
  };

  const getFirstName = (name) => {
    if (!name) return '';
    return name.split(' ')[0];
  };

  const handleManageAccount = () => {
    setShowProfilePopup(false);
    navigate('/account');
  };

  const handleSubscriptionPayment = () => {
    setShowProfilePopup(false);
    navigate('/account/subscription');
  };

  const submitFeedback = async (event) => {
    event.preventDefault();
    setSendingFeedback(true);
    setFeedbackStatus('');
    try {
      await axiosInstance.post('/api/feedback', { message: feedbackText, name: feedbackName });
      setFeedbackText('');
      setFeedbackName('');
      setFeedbackStatus('Thanks, your feedback was submitted.');
      setTimeout(() => setShowFeedbackForm(false), 1400);
    } catch (error) {
      setFeedbackStatus(error.response?.data?.message || 'Could not submit feedback. Please try again.');
    } finally {
      setSendingFeedback(false);
    }
  };

  const handleOpenSettingsFromProfile = () => {
    setShowProfilePopup(false);
    navigate('/workspace/settings');
  };

  /**
   * Handle switching to another account
   */
  const handleSwitchAccount = (accountEmail) => {
    setShowProfilePopup(false);
    if (onSwitchAccount) {
      onSwitchAccount(accountEmail);
    }
  };

  /**
   * Get other accounts (excluding current account)
   */
  const getOtherAccounts = () => {
    return allAccounts.filter(account => account.email !== currentUser.email);
  };

  /**
   * Handle deleting an account
   */
  const handleDeleteAccount = (e, accountEmail) => {
    e.stopPropagation(); // Prevent switching account when clicking delete
    if (onDeleteAccount) {
      onDeleteAccount(accountEmail);
    }
  };

  return (
    <nav className="gmail-navbar">
      <div className="nav-left">
        <button className="menu-btn" aria-label="Menu" onClick={onMenuClick}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
          </svg>
        </button>
        <div className="logo" onClick={() => navigate('/inbox')}>
          <img className="logo-image" src="/atozasmail-logo.png" alt="ATOZASMail" />
        </div>
      </div>

      <div className="nav-center">
        {/* The options panel is a sibling of the form, not a child: it contains
            its own form and forms cannot nest. */}
        <div className="search-shell">
          <form className="search-form" onSubmit={handleSearch}>
            <div className="search-container">
              <button type="button" className="search-icon-btn" aria-label="Search">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
              </button>
              <input
                type="text"
                className="search-input"
                placeholder="Search mail"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button
                type="button"
                className={`search-options-btn${showSearchOptions ? ' is-open' : ''}`}
                title="Show search options"
                aria-label="Show search options"
                aria-expanded={showSearchOptions}
                aria-haspopup="dialog"
                onClick={() => setShowSearchOptions((v) => !v)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M3 6h18v2H3V6zm3 5h12v2H6v-2zm3 5h6v2H9v-2z" />
                </svg>
              </button>
            </div>
          </form>

          {showSearchOptions && (
            <SearchOptionsPanel
              query={searchQuery}
              onClose={() => setShowSearchOptions(false)}
              onSearch={(builtQuery) => {
                setShowSearchOptions(false);
                runSearch(builtQuery);
              }}
            />
          )}
        </div>
      </div>

      <div className="nav-right">
        <button
          ref={workspaceBtnRef}
          className={`nav-icon-btn ws-launcher-btn${showWorkspace ? ' is-open' : ''}`}
          aria-label="ATOZAS Workspace"
          title="ATOZAS Workspace (Ctrl+Shift+W)"
          aria-expanded={showWorkspace}
          aria-haspopup="dialog"
          onClick={toggleWorkspace}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <circle className="ws-launcher-dot" cx="5" cy="5" r="2" />
            <circle className="ws-launcher-dot" cx="12" cy="5" r="2" />
            <circle className="ws-launcher-dot" cx="19" cy="5" r="2" />
            <circle className="ws-launcher-dot" cx="5" cy="12" r="2" />
            <circle className="ws-launcher-dot" cx="12" cy="12" r="2" />
            <circle className="ws-launcher-dot" cx="19" cy="12" r="2" />
            <circle className="ws-launcher-dot" cx="5" cy="19" r="2" />
            <circle className="ws-launcher-dot" cx="12" cy="19" r="2" />
            <circle className="ws-launcher-dot" cx="19" cy="19" r="2" />
          </svg>
        </button>
        <div className="user-avatar" title={currentUser.email} onClick={toggleProfilePopup}>
          <UserAvatar user={currentUser} size="md" />
        </div>

        {/* Profile Popup */}
        {showProfilePopup && (
          <div className="profile-popup-overlay" onClick={() => setShowProfilePopup(false)}>
            <div className="profile-popup" ref={profilePopupRef} onClick={(e) => e.stopPropagation()}>
              {/* Header with email and close button */}
              <div className="profile-popup-header">
                <div className="profile-popup-email">{currentUser.email}</div>
                <button 
                  className="profile-popup-close" 
                  onClick={() => setShowProfilePopup(false)}
                  aria-label="Close"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                </button>
              </div>

              {/* Profile Picture Section */}
              <div className="profile-picture-section">
                <ProfilePhotoEditor
                  user={currentUser}
                  onUserUpdate={onProfileUpdate}
                  size="lg"
                  showHint={false}
                />
                <div className="profile-greeting">Hi, {getFirstName(currentUser.firstName || currentUser.username)}!</div>
              </div>

              {/* Manage Account Button */}
              <button type="button" className="manage-account-btn" onClick={handleManageAccount}>
              Securely Manage Your Email Account
              </button>

              {!hideSubscriptionButton && (
                <button type="button" className="subscription-payment-btn" onClick={handleSubscriptionPayment}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
                  </svg>
                  <span>Subscription payment</span>
                </button>
              )}

              {/* Divider */}
              <div className="profile-popup-divider"></div>

              {/* Accounts Section */}
              <div className="accounts-section">
                <button 
                  className="hide-accounts-btn"
                  onClick={() => setShowMoreAccounts(!showMoreAccounts)}
                >
                  <span>Hide more accounts</span>
                  <svg 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                    className={showMoreAccounts ? '' : 'rotated'}
                  >
                    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                  </svg>
                </button>

                {showMoreAccounts && (
                  <div className="accounts-list">
                    {/* Current Account */}
                    <div className="account-item current-account">
                      <div className="account-avatar">
                        <UserAvatar user={currentUser} size="account" />
                      </div>
                      <div className="account-info">
                        <div className="account-name">{currentUser.username}</div>
                        <div className="account-email">{currentUser.email}</div>
                      </div>
                    </div>
                    
                    {/* Other Accounts */}
                    {getOtherAccounts().map((account, index) => (
                      <div 
                        key={index} 
                        className="account-item account-item-clickable"
                        onClick={() => handleSwitchAccount(account.email)}
                      >
                        <div className="account-avatar">
                          <UserAvatar user={account} size="account" />
                        </div>
                        <div className="account-info">
                          <div className="account-name">{account.username}</div>
                          <div className="account-email">{account.email}</div>
                        </div>
                        <button
                          className="account-delete-btn"
                          onClick={(e) => handleDeleteAccount(e, account.email)}
                          aria-label="Delete account"
                          title="Delete account"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="profile-popup-divider"></div>

              {/* Action Buttons */}
              <div className="profile-actions">
                <button type="button" className="profile-action-btn" onClick={() => { setFeedbackStatus(''); setShowFeedbackForm(true); }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 11H6v-2h12v2zm0-4H6V7h12v2z"/></svg>
                  <span>Feedback</span>
                </button>
                <button type="button" className="profile-action-btn profile-settings-btn" onClick={handleOpenSettingsFromProfile}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
                  </svg>
                  <span>Settings</span>
                </button>
                <button className="profile-action-btn" onClick={handleAddAnotherAccount}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                  </svg>
                  <span>Add another account</span>
                </button>
                <button className="profile-action-btn" onClick={handleSignOutAll}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
                  </svg>
                  <span>Sign Out Everywhere</span>
                </button>
              </div>

              {/* Storage Info */}
              {/* <div className="storage-info">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/>
                </svg>
                <span>1% of 15 GB used</span>
              </div> */}
            </div>
          </div>
        )}
        {showFeedbackForm && createPortal((
          <div className="feedback-modal-overlay" onClick={() => !sendingFeedback && setShowFeedbackForm(false)}>
            <form className="feedback-modal" onSubmit={submitFeedback} onClick={(event) => event.stopPropagation()}>
              <div className="feedback-form-intro">
                <div className="feedback-form-topline" />
                <div className="feedback-modal-header">
                  <h2>Feedback</h2>
                  <button type="button" onClick={() => setShowFeedbackForm(false)} aria-label="Close feedback form">×</button>
                </div>
                <p>Post your feedback, comments, questions... Thank you!<br />We appreciate you helping us improve ATOZASMail.</p>
                <div className="feedback-account-info">
                  <div><strong>{currentUser.email}</strong> <button type="button" onClick={() => { setShowFeedbackForm(false); navigate('/login?addAccount=true'); }}>Switch account</button></div>
                  <span><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>Not shared</span>
                </div>
                <div className="feedback-required-note"><span>*</span> Indicates required question</div>
              </div>
              <label className="feedback-question-card">
                <span>Name (and email if you want a response) <b>*</b></span>
                <input value={feedbackName} onChange={(event) => setFeedbackName(event.target.value)} placeholder="Your answer" required maxLength={120} autoFocus />
              </label>
              <label className="feedback-question-card feedback-comment-card">
                <span>Comment <b>*</b></span>
                <textarea value={feedbackText} onChange={(event) => setFeedbackText(event.target.value)} placeholder="Your answer" maxLength={5000} required minLength={3} rows={3} />
              </label>
              {feedbackStatus && <div className="feedback-status" role="status">{feedbackStatus}</div>}
              <div className="feedback-modal-actions">
                <button type="submit" disabled={sendingFeedback || !feedbackName.trim() || feedbackText.trim().length < 3}>{sendingFeedback ? 'Sending...' : 'Submit'}</button>
                <button type="button" className="feedback-clear-btn" onClick={() => { setFeedbackText(''); setFeedbackName(''); setFeedbackStatus(''); }}>Clear form</button>
              </div>
            </form>
          </div>
        ), document.body)}
      </div>

      <WorkspaceLauncher
        open={showWorkspace}
        onClose={() => setShowWorkspace(false)}
        currentUser={currentUser}
        anchorRef={workspaceBtnRef}
      />
    </nav>
  );
};

export default Navigation;

